import { Button, ButtonProps } from "@chakra-ui/react";
import React from "react";
import { FormattedMessage, IntlShape } from "react-intl";
import { useSelector } from "react-redux";

import { checkPermission } from "../state/appState";
import { ControlType } from "../utils/authorizationUtils";

import Tooltip from "./Tooltip";

interface Props extends ButtonProps {
  controlType: ControlType;
  formattedMessage: string | JSX.Element;
  doSubmit: () => void;
  disabled?: boolean;
  rightMenuButton?: boolean;
  disabledMessage?: JSX.Element;
}

export const formattedMessageForErrorCodeAsString = (
  errorCode: string,
  intl: IntlShape
): string => {
  if (errorCode === "simulationRunning") {
    return intl.formatMessage({
      id: "tooltip.simulationRunning",
      defaultMessage: "Simulation is running",
    });
  }
  if (errorCode === "masterUserRequired") {
    return intl.formatMessage({
      id: "tooltip.masterUserRequired",
      defaultMessage: "Only master user is allowed to edit",
    });
  }
  if (errorCode === "NRRExpired") {
    return intl.formatMessage({
      id: "tooltip.NRRExpire",
      defaultMessage: "You do not have access to this functionality",
    });
  }
  if (errorCode === "simulationStarted") {
    return intl.formatMessage({
      id: "tooltip.simulationStarted",
      defaultMessage: "Functionality not available after simulation started",
    });
  }
  return errorCode;
};

export const formattedMessageForErrorCode = (errorCode: string): JSX.Element | null => {
  if (errorCode === "simulationRunning") {
    return (
      <FormattedMessage id="tooltip.simulationRunning" defaultMessage="Simulation is running" />
    );
  }
  if (errorCode === "masterUserRequired") {
    return (
      <FormattedMessage
        id="tooltip.masterUserRequired"
        defaultMessage="Only master user is allowed to edit"
      />
    );
  }
  if (errorCode === "NRRExpired") {
    return (
      <FormattedMessage
        id="tooltip.NRRExpired"
        defaultMessage="You do not have access to this functionality"
      />
    );
  }

  return null;
};

const PermissionButton: React.FC<Props> = (props) => {
  const {
    disabled = false,
    variant = "secondary",
    controlType,
    formattedMessage,
    doSubmit,
    disabledMessage,
    ...rest
  } = props;

  // Permission check
  const permission = useSelector(checkPermission)(controlType);
  const errorCode = !permission.allowed ? permission.errorCode : "";
  // generate different error messages depends on the error code
  const message = formattedMessageForErrorCode(errorCode) || disabledMessage;

  return (
    <Button
      variant={variant}
      onClick={doSubmit}
      isDisabled={disabled || !permission.allowed}
      {...rest}
    >
      {formattedMessage}
      {(!permission.allowed || disabled) && message ? <Tooltip message={message} /> : null}
    </Button>
  );
};

export default PermissionButton;
